import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Layout from "../components/Layout";
import { api } from "../lib/api";
import VerifiedTierBadge from "../components/VerifiedTierBadge";
import TrustScoreBadge from "../components/TrustScoreBadge";
import { hideOnError } from "../lib/imgFallback";
import { Crown, TrendingUp, Sparkles, Trophy } from "lucide-react";
import { displayName } from "../lib/displayName";

const VIEWS = [
  { id: "top", label: "Top Scores", icon: Trophy },
  { id: "climbers", label: "This week's climbers", icon: TrendingUp },
];

export default function Leaderboard() {
  const [view, setView] = useState("top");
  const [tradition, setTradition] = useState("");
  const [traditions, setTraditions] = useState([]);
  const [data, setData] = useState({ entries: [], total: 0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/leaderboard/traditions?limit=8").then(({ data }) => setTraditions(data.traditions || [])).catch(() => {});
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const params = { view, limit: 20 };
    if (tradition) params.tradition = tradition;
    api.get("/leaderboard/trust", { params })
      .then(({ data }) => { if (!cancelled) setData(data); })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [view, tradition]);

  return (
    <Layout>
      <div className="space-y-6">
        <div>
          <div className="label-mystic">Public rankings</div>
          <h1 className="font-serif text-4xl sm:text-5xl mt-2">Trust Leaderboard</h1>
          <p className="text-foreground/70 mt-2 max-w-2xl">
            Two ways to look at it: the practitioners with the highest absolute trust scores right now, and the ones climbing fastest this week. Both are computed from the same six signals you see on every profile.
          </p>
        </div>

        {/* View switcher + tradition filter */}
        <div className="flex flex-wrap items-center gap-3" data-testid="leaderboard-controls">
          <div className="inline-flex items-center gap-1 rounded-sm border border-secondary/25 overflow-hidden" data-testid="view-switcher">
            {VIEWS.map(({ id, label, icon: Icon }) => (
              <button
                key={id}
                onClick={() => setView(id)}
                className={`inline-flex items-center gap-1.5 px-4 py-2 text-xs uppercase tracking-wider ${view === id ? "bg-secondary/15 text-secondary" : "text-foreground/65 hover:text-foreground"}`}
                data-testid={`view-${id}`}
              >
                <Icon size={12} strokeWidth={1.5} /> {label}
              </button>
            ))}
          </div>

          <div className="flex items-center gap-1.5 flex-wrap">
            <button
              onClick={() => setTradition("")}
              className={`px-2.5 py-1 rounded-full border text-[11px] uppercase tracking-wider ${!tradition ? "border-secondary text-secondary bg-secondary/10" : "border-secondary/25 text-foreground/65 hover:border-secondary/50"}`}
              data-testid="tradition-any"
            >All traditions</button>
            {traditions.map((t) => (
              <button
                key={t.key}
                onClick={() => setTradition(tradition === t.label ? "" : t.label)}
                className={`px-2.5 py-1 rounded-full border text-[11px] uppercase tracking-wider ${tradition === t.label ? "border-secondary text-secondary bg-secondary/10" : "border-secondary/25 text-foreground/65 hover:border-secondary/50"}`}
                data-testid={`tradition-${t.key}`}
                title={`${t.count} practitioners`}
              >{t.label}</button>
            ))}
          </div>
        </div>

        {/* Rankings */}
        <div className="space-y-1.5" data-testid="leaderboard-list">
          <div className="grid grid-cols-[40px_1fr_120px] sm:grid-cols-[40px_1fr_180px_120px] gap-3 px-4 py-2 text-[10px] uppercase tracking-[0.18em] text-foreground/45">
            <span>Rank</span>
            <span>Practitioner</span>
            <span className="hidden sm:block">Tradition</span>
            <span className="text-right">{view === "climbers" ? "7d Δ" : "Score"}</span>
          </div>

          {loading && <div className="parchment p-6 text-foreground/55 italic">Loading rankings...</div>}

          {!loading && data.entries.length === 0 && (
            <div className="parchment p-12 text-center text-foreground/55 italic font-serif text-xl" data-testid="leaderboard-empty">
              {view === "climbers"
                ? "No climbers yet this week — be the first to lift your score."
                : "No practitioners match this filter."}
            </div>
          )}

          {data.entries.map((p, i) => (
            <Link
              key={p.userId}
              to={`/profile/${p.userId}`}
              className="parchment grid grid-cols-[40px_1fr_120px] sm:grid-cols-[40px_1fr_180px_120px] gap-3 px-4 py-3 items-center hover:border-secondary/40 transition-colors"
              data-testid={`leaderboard-row-${p.userId}`}
            >
              <span className={`font-serif text-2xl ${i < 3 ? "text-secondary" : "text-foreground/55"}`}>
                {i + 1}
                {i === 0 && <Crown size={12} className="inline -mt-3 ml-1 text-secondary" />}
              </span>

              <div className="flex items-center gap-3 min-w-0">
                <div className="w-10 h-10 rounded-full overflow-hidden bg-card border border-secondary/20 flex-shrink-0">
                  {(p.avatarUrl || p.user?.avatarUrl) ? (
                    <img src={p.avatarUrl || p.user?.avatarUrl} alt="" onError={hideOnError} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center font-serif text-base text-secondary">
                      {(p.businessName || p.user?.name || "?")[0]?.toUpperCase()}
                    </div>
                  )}
                </div>
                <div className="min-w-0">
                  <div className="font-serif text-base truncate">{displayName(p)}</div>
                  <div className="inline-flex items-center gap-2">
                    <VerifiedTierBadge tier={p.verifiedTier} size={10} compact />
                  </div>
                </div>
              </div>

              <div className="hidden sm:block text-xs text-foreground/55 truncate">
                {p.tradition || <span className="italic text-foreground/35">—</span>}
              </div>

              <div className="text-right">
                {view === "climbers" ? (
                  <span className="inline-flex items-center gap-1 text-secondary font-serif text-xl" data-testid={`row-delta-${p.userId}`}>
                    <Sparkles size={12} />+{p.delta7d}
                  </span>
                ) : (
                  <TrustScoreBadge score={p.trustScore || 0} providerId={p.userId} compact />
                )}
              </div>
            </Link>
          ))}
        </div>
      </div>
    </Layout>
  );
}
